import { GeometryContainer, GeometryType, CompiledGeometryContainer } from "@spi/Common/Geometry";
import { Light, LightType } from "./Light/Light";
import { ConditionalGate, NavigationMesh, PathNode } from "@spi/Common/NavigationMesh";
import { BoundingBox } from "@spi/Common/Util";
import { ILogger } from "@spi/Common/Logging/Logger";
import { vec3, vec4 } from "gl-matrix";

export class SceneState {
    private _navigationMesh: NavigationMesh;
    public geometryContainers: GeometryContainer[];
    public compiledGeometry: CompiledGeometryContainer[];
    public lights: Light[];
    public navigationGraph: PathNode[];
    public textureAssets: TextureAssetDescriptor[];
    public boundingBox: BoundingBox;

    constructor(public reference: string, public name: string) {
        this._navigationMesh = null;
        this.geometryContainers = [];
        this.lights = [];
        this.navigationGraph = [];
        this.compiledGeometry = [];
        this.textureAssets = [];
    }

    public getRequiredTextures(): string[] {
        return [];
    }

    public getRequiredAssets(): string[] {
        return [];
    }

    public createNavigationGraph(logger: ILogger) {
        this._navigationMesh = new NavigationMesh({
            bounds: this.boundingBox,
            segmentSize: 100,
            voxelSize: 1,
            maxConvexExpansion: 20,
            maxNeighbourStepHeight: 1,
            minTraversableHeight: 10
        }, logger);

        logger.info('Generating navigation mesh. Fresh RAM nomnomnom...');
        let geoCount = 0;
        for (let geo of this.geometryContainers) {
            geoCount++;
            switch (geo.type) {
                case GeometryType.Uncompiled:
                case GeometryType.Generic:
                    logger.progress(`Processing generic geometry: ${geo.reference}... (${geoCount} / ${this.geometryContainers.length})`);
                    this._navigationMesh.addGeometry(geo.faces);
                    break;
                case GeometryType.Conditional:
                    logger.progress(`Processing conditional geometry: ${geo.reference}... (${geoCount} / ${this.geometryContainers.length})`);
                    let gate = new ConditionalGate();
                    gate.gateReference = geo.reference;
                    gate.faces = geo.faces;
                    this._navigationMesh.addConditionalGates([gate]);
                    break;
            }
        }
        logger.clear();

        logger.info('Generating node graph...');
        this._navigationMesh.generate();
        logger.info('Navigation mesh complete');

        this.navigationGraph = this._navigationMesh.pathNodes;
    }

    public bakeLights(logger: ILogger) {
        logger.info('Baking lights');

        for (let geo of this.geometryContainers) {

            let faceCount = 0;

            for (let face of geo.faces) {

                if(faceCount % 1000 == 0) {
                    logger.progress(`Baking lights for geometry: ${geo.reference} (${faceCount}/${geo.faces.length} faces)`);
                }
                faceCount++;

                for (let vertex of face.vertices) {

                    if (vertex.light) continue;

                    let lightWeighting = vec3.create();
                    for (let light of this.lights) {

                        // float distanceToLight = length(lightLocation - vPosition.xyz);
                        // float attenuation = 1.0 / (1.0 + uPointLightingColor[i].a * pow(distanceToLight, 2.0));

                        // vec3 lightDirection = normalize(lightLocation - vPosition.xyz);
                        // float directionalLightWeighting = max(dot(normalize(vTransformedNormal), lightDirection), 0.0);
                        // lightWeighting += attenuation * (uPointLightingColor[i].xyz * directionalLightWeighting);

                        let lightPos = vec3.fromValues(light.position[0], light.position[1], light.position[2]);
                        let dist = vec3.dist(vertex.position, lightPos);

                        if(dist > 50) continue; // TODO: base this on attenuation

                        let lightColor = vec3.fromValues(light.color[0], light.color[1], light.color[2]);

                        
                        let attenuation = 1 / (1 + light.color[3] * Math.pow(dist, 2));

                        let lightDir = vec3.sub(vec3.create(), lightPos, vertex.position);
                        let normalizedLightDir = vec3.normalize(vec3.create(), lightDir);

                        let directionalLightWeighting = Math.max(vec3.dot(vec3.normalize(vec3.create(), vertex.normal), normalizedLightDir), 0);

                        let lightResult = vec3.multiply(vec3.create(), lightColor, [directionalLightWeighting, directionalLightWeighting, directionalLightWeighting]);
                        vec3.multiply(lightResult, lightResult, [attenuation, attenuation, attenuation]);
                        vec3.add(lightWeighting, lightWeighting, lightResult);
                    }

                    vertex.setLight(lightWeighting);
                }
            }
        }
        logger.clear();
        logger.info('Lights done');
    }
}

export type TextureAssetDescriptor = {
    reference: string,
    uri: string,
    translucent: boolean
};